// frontend/src/components/ComplaintPermission.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  RemoveRedEye as EyeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  getApplications,
  getDisciplines,
  getGroups,
} from "../../../services/complaintService";
import ComplaintPermissionViewDialog from "../../../components/ComplaintPermissionViewDialog";
import { decryptAppId, decryptText, encryptAppId, encryptText } from '../../../utils/encryption';


const ComplaintPermission = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [disciplineDialogOpen, setDisciplineDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [disciplineList, setDisciplineList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);

  // Dialog state for Complaint Permission View
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [selectedLabTypeId, setSelectedLabTypeId] = useState(null);
  const [selectedOrgIdCab, setSelectedOrgIdCab] = useState(null);

  const orgId = localStorage.getItem("orgId");

  const fetchApplications = useCallback(async () => {
    if (!orgId) {
      setError("User session invalid. Please login again.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await getApplications({ orgId });
      if (response.success) {
        setApplications(response.data || []);
      } else {
        setError(response.message || "Failed to load applications");
      }
    } catch (err) {
      console.error(err);
      setError("Error loading applications");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleView = (appId, labTypeId, orgIdCab) => {
    setSelectedAppId(appId);
    setSelectedLabTypeId(labTypeId);
    setSelectedOrgIdCab(orgIdCab);
    setViewDialogOpen(true);
  };

  const handleHistory = (appId) => {
    const encryptedAppId = encryptAppId(appId.toString());
    window.open(`/main/complaint/applications/history?appId=${encryptedAppId}`);
  };

  const handleShowDisciplines = async (appId, labTypeId) => {
    setDialogLoading(true);
    try {
      const response = await getDisciplines({ appId, labTypeId });
      if (response.success) {
        setDisciplineList(response.data || []);
        setDisciplineDialogOpen(true);
      } else {
        setError(response.message || "Failed to load disciplines");
      }
    } catch (err) {
      setError("Error loading disciplines");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleShowGroups = async (appId, labTypeId) => {
    setDialogLoading(true);
    try {
      const response = await getGroups({ appId, labTypeId });
      if (response.success) {
        setGroupList(response.data || []);
        setGroupDialogOpen(true);
      } else {
        setError(response.message || "Failed to load groups");
      }
    } catch (err) {
      setError("Error loading groups");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDetailsView = () => {
    setViewDialogOpen(false);
  }

   // Handle back navigation
    const handleBackClick = () => {
        navigate(-1); // Go back to previous page
    };

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
        {/* <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mr: 2 }}
        >
          Back
        </Button> */}
        <Typography
          variant="h4"
          gutterBottom
          align="center"
          sx={{ flexGrow: 1, m: 0 }}
        >
          Complaints for Confirmation
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : applications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6">No applications found</Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, overflowX: "auto", maxHeight: "70vh" }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Action
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Sr. No.
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Lab ID
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  User Name
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Laboratory Name
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Standard
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", bgcolor: "lightgoldenrodyellow" }}
                >
                  Application Type
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Address
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  State
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  City
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "bisque" }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Status Date
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Discipline
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Group
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Field
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Application Submitted on
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((app, index) => (
                <TableRow key={app.AppId} hover>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon />}
                        onClick={() =>
                          handleView(app.AppId, app.LabTypeId, app.OrgId)
                        }
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={() => handleHistory(app.AppId)}
                      >
                        History
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{app.ApplicationNo || "-"}</TableCell>
                  <TableCell>{app.LoginName || "-"}</TableCell>
                  <TableCell>{app.LaboratoryName || "-"}</TableCell>
                  <TableCell>{app.Standard || "-"}</TableCell>
                  <TableCell sx={{ bgcolor: "lightgoldenrodyellow" }}>
                    {app.AssessmentType || "-"}
                  </TableCell>
                  <TableCell>{app.Address || "-"}</TableCell>
                  <TableCell>{app.State || "-"}</TableCell>
                  <TableCell>{app.City || "-"}</TableCell>
                  <TableCell sx={{ bgcolor: "bisque" }}>
                    {app.Status || "-"}
                  </TableCell>
                  <TableCell>
                    {app.UpdateDate
                      ? new Date(app.UpdateDate).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        })
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleShowDisciplines(app.AppId, app.LabTypeId)
                      }
                    >
                      <EyeIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {app.Viseye === "1" && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleShowGroups(app.AppId, app.LabTypeId)
                        }
                      >
                        <EyeIcon />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{app.LabType || "-"}</TableCell>
                  <TableCell>
                    {app.SubmitDate
                      ? new Date(
                          Array.isArray(app.SubmitDate)
                            ? app.SubmitDate[0]
                            : app.SubmitDate,
                        ).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        })
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Discipline Dialog (inner) */}
      <Dialog
        open={disciplineDialogOpen}
        onClose={() => setDisciplineDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Discipline</DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={30} />
            </Box>
          ) : disciplineList.length === 0 ? (
            <Typography>No disciplines found</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S No.</TableCell>
                    <TableCell>Discipline</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {disciplineList.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{item.Dis}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisciplineDialogOpen(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Dialog (inner) */}
      <Dialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Group</DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={30} />
            </Box>
          ) : groupList.length === 0 ? (
            <Typography>No groups found</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S No.</TableCell>
                    <TableCell>Discipline</TableCell>
                    <TableCell>Group</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupList.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{item.Dis}</TableCell>
                      <TableCell>{item.Grp}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    {/* Complaint Permission View Dialog */}
    <ComplaintPermissionViewDialog
      open={viewDialogOpen}
      onClose={handleCloseDetailsView}
      onRefresh={fetchApplications}
      appId={selectedAppId}
      labTypeId={selectedLabTypeId}
      orgIdCab={selectedOrgIdCab}
    />
  </Box>
  );
};

export default ComplaintPermission;
